import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent
} from "@dnd-kit/core"
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { HolderOutlined } from "@ant-design/icons"
import { Button, Tooltip } from "antd"
import React, { useLayoutEffect, useRef, useState } from "react"
import "react-grid-layout/css/styles.css"
import ReactGridLayout, { type Layout } from "react-grid-layout/legacy"
import { useTranslation } from "react-i18next"

import { homeColor } from "../../home-theme.ts"
import { HOME_GRID_COLUMNS, readLayout } from "./homeLayout"
import {
  HOME_GUTTER,
  HOME_ROW_HEIGHT,
  type HomePage,
  type HomePageSectionId,
  type PageSection,
  applyGridChange,
  moveSection,
  moveToSection,
  visibleSections,
  widgetsIn
} from "./homePageLayout"
import { GRID_HANDLE_CLASS } from "./WidgetEditChrome"

/**
 * Below this width the page is not drawn as grids: each section stacks its
 * widgets in reading order, and nothing reports a layout, so a narrow window
 * can never rewrite the desktop arrangement.
 */
export const HOME_PAGE_GRID_MIN_WIDTH = 900

/**
 * The page's own width, from a ResizeObserver: react-grid-layout's
 * `WidthProvider` hears window resizes only, and collapsing the sidebar
 * changes the content column without one.
 */
const useContainerWidth = () => {
  const ref = useRef<HTMLDivElement | null>(null)
  const [width, setWidth] = useState(0)
  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    setWidth(Math.round(element.getBoundingClientRect().width))
    if (typeof ResizeObserver !== "function") return
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width
      if (typeof next === "number") setWidth(Math.round(next))
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])
  return { ref, width }
}

export interface SectionHeadingArgs {
  section: PageSection
  /** The group's drag handle; `null` outside Edit layout. */
  handle: React.ReactNode | null
  index: number
  count: number
}

interface HomePageGridProps {
  /** The whole page, every section included (empty ones are not drawn). */
  page: HomePage
  isEditing: boolean
  minSizeOf: (id: string) => { w: number; h: number }
  isAutoHeight: (id: string) => boolean
  /** A section's heading, or `null` for a section that has none. */
  renderHeading: (args: SectionHeadingArgs) => React.ReactNode | null
  renderWidget: (id: string) => React.ReactNode
  onPageChange: (next: HomePage) => void
}

interface GrabState {
  id: string
  from: HomePageSectionId
  dx: number
  dy: number
}

export const HomePageGrid = ({
  page,
  isEditing,
  minSizeOf,
  isAutoHeight,
  renderHeading,
  renderWidget,
  onPageChange
}: HomePageGridProps) => {
  const { ref, width } = useContainerWidth()
  const stacked = width < HOME_PAGE_GRID_MIN_WIDTH
  const gridRefs = useRef(new Map<HomePageSectionId, HTMLDivElement>())
  const grab = useRef<GrabState | null>(null)
  const [dropTarget, setDropTarget] = useState<HomePageSectionId | null>(null)
  const sections = visibleSections(page)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  /** The visible section whose grid lies under a point, with its box. */
  const sectionAt = (clientX: number, clientY: number) => {
    for (const [id, element] of gridRefs.current) {
      const rect = element.getBoundingClientRect()
      // A section's widgets can be short; the drop area extends a row below.
      if (
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top - HOME_GUTTER &&
        clientY <= rect.bottom + 48
      )
        return { id, rect }
    }
    return null
  }

  const handleSectionDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    const to = page.sections.findIndex((section) => section.id === over.id)
    onPageChange(moveSection(page, active.id as HomePageSectionId, to))
  }

  const pointOf = (event: Event | undefined) => {
    const e = event as MouseEvent | TouchEvent | undefined
    if (!e) return null
    if ("clientX" in e) return { x: e.clientX, y: e.clientY }
    const touch = e.changedTouches?.[0]
    return touch ? { x: touch.clientX, y: touch.clientY } : null
  }

  const grid = (section: PageSection) => {
    const items = widgetsIn(page, section.id)
    if (stacked) {
      return (
        <div className="home-page-grid__stack">
          {[...items]
            .sort((a, b) => a.y - b.y || a.x - b.x)
            .map((item) => (
              <div
                key={item.i}
                data-home-item={item.i}
                className="home-page-grid__unit"
              >
                {renderWidget(item.i)}
              </div>
            ))}
        </div>
      )
    }
    const layout: Layout = items.map((item) => {
      const min = minSizeOf(item.i)
      return isAutoHeight(item.i)
        ? {
            i: item.i,
            x: item.x,
            y: item.y,
            w: item.w,
            h: item.h,
            minW: min.w,
            minH: 1,
            resizeHandles: ["e"]
          }
        : {
            i: item.i,
            x: item.x,
            y: item.y,
            w: item.w,
            h: item.h,
            minW: min.w,
            minH: min.h
          }
    })
    return (
      <ReactGridLayout
        width={width}
        layout={layout}
        cols={HOME_GRID_COLUMNS}
        rowHeight={HOME_ROW_HEIGHT}
        margin={[HOME_GUTTER, HOME_GUTTER]}
        containerPadding={[0, 0]}
        // Only a widget's own header handle starts a drag, so a click on a
        // record row, a filter or a chart inside it is never a drag.
        draggableHandle={`.${GRID_HANDLE_CLASS}`}
        isDraggable={isEditing}
        isResizable={isEditing}
        resizeHandles={["se"]}
        compactType="vertical"
        preventCollision={false}
        allowOverlap={false}
        useCSSTransforms
        onDragStart={(_layout, _old, item, _p, event, element) => {
          const point = pointOf(event)
          const rect = element?.getBoundingClientRect()
          grab.current =
            item && point && rect
              ? {
                  id: item.i,
                  from: section.id,
                  dx: point.x - rect.left,
                  dy: point.y - rect.top
                }
              : null
        }}
        onDrag={(_layout, _old, _item, _p, event) => {
          const point = pointOf(event)
          const over = point ? sectionAt(point.x, point.y) : null
          const target = over && over.id !== section.id ? over.id : null
          setDropTarget((current) => (current === target ? current : target))
        }}
        onDragStop={(next, _old, item, _p, event) => {
          setDropTarget(null)
          const point = pointOf(event)
          const state = grab.current
          grab.current = null
          const over = point ? sectionAt(point.x, point.y) : null
          if (item && state && point && over && over.id !== section.id) {
            // Dropped over another section: the whole widget moves there, to
            // the cell under where its top-left corner was released.
            const column =
              (width - (HOME_GRID_COLUMNS - 1) * HOME_GUTTER) /
                HOME_GRID_COLUMNS +
              HOME_GUTTER
            const row = HOME_ROW_HEIGHT + HOME_GUTTER
            onPageChange(
              moveToSection(page, item.i, over.id, {
                x: Math.round((point.x - state.dx - over.rect.left) / column),
                y: Math.round((point.y - state.dy - over.rect.top) / row)
              })
            )
            return
          }
          if (item)
            onPageChange(
              applyGridChange(
                page,
                section.id,
                readLayout(next),
                item.i,
                "drag"
              )
            )
        }}
        onResizeStop={(next, _old, item) => {
          if (item)
            onPageChange(
              applyGridChange(
                page,
                section.id,
                readLayout(next),
                item.i,
                "resize"
              )
            )
        }}
      >
        {items.map((item) => (
          <div
            key={item.i}
            data-home-item={item.i}
            className="home-page-grid__unit"
          >
            {renderWidget(item.i)}
          </div>
        ))}
      </ReactGridLayout>
    )
  }

  return (
    <div
      ref={ref}
      className={`home-page-grid${isEditing ? " home-page-grid--editing" : ""}`}
      data-testid="home-page-grid"
      data-layout-mode={stacked ? "stacked" : "grid"}
    >
      {width > 0 || stacked ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleSectionDragEnd}
        >
          <SortableContext
            items={sections.map((section) => section.id)}
            strategy={verticalListSortingStrategy}
          >
            {sections.map((section, index) => (
              <SortableSection
                key={section.id}
                section={section}
                index={index}
                count={sections.length}
                isEditing={isEditing}
                isDropTarget={dropTarget === section.id}
                renderHeading={renderHeading}
                gridRef={(element) => {
                  if (element) gridRefs.current.set(section.id, element)
                  else gridRefs.current.delete(section.id)
                }}
              >
                {grid(section)}
              </SortableSection>
            ))}
          </SortableContext>
        </DndContext>
      ) : null}
    </div>
  )
}

const SortableSection = ({
  section,
  index,
  count,
  isEditing,
  isDropTarget,
  renderHeading,
  gridRef,
  children
}: {
  section: PageSection
  index: number
  count: number
  isEditing: boolean
  isDropTarget: boolean
  renderHeading: (args: SectionHeadingArgs) => React.ReactNode | null
  gridRef: (element: HTMLDivElement | null) => void
  children: React.ReactNode
}) => {
  const { t } = useTranslation()
  const probe = renderHeading({ section, handle: null, index, count })
  const movable = isEditing && probe !== null
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: section.id, disabled: !movable })

  const handle = movable ? (
    <Tooltip title={t("home.layout.dragSection", "Drag to move this section")}>
      <Button
        ref={setActivatorNodeRef}
        type="text"
        size="small"
        icon={<HolderOutlined />}
        data-testid={`home-section-handle-${section.id}`}
        style={{
          cursor: "grab",
          color: homeColor.inkMuted,
          width: 24,
          minWidth: 24
        }}
        {...attributes}
        {...listeners}
        aria-label={t(
          "home.layout.moveSectionNamed",
          "Move section {{title}}",
          {
            title: section.label ?? section.id
          }
        )}
      />
    </Tooltip>
  ) : null

  return (
    <section
      ref={setNodeRef}
      className={[
        "home-page-section",
        isDragging ? "home-page-section--dragging" : "",
        isDropTarget ? "home-page-section--drop" : ""
      ].join(" ")}
      data-home-section={section.id}
      style={{
        transform: CSS.Translate.toString(transform),
        transition
      }}
    >
      {probe !== null ? renderHeading({ section, handle, index, count }) : null}
      <div ref={gridRef} className="home-page-section__grid">
        {children}
      </div>
    </section>
  )
}
